/* eslint-disable react/no-unescaped-entities */
// v3.21-1686 · Funding Evidence Missing — SSOT diagnostic correction.
//
// This screen classifies Deals whose funding state does not match the
// authoritative SSOT.  It is purely diagnostic — it never writes to
// financial fields.  A row clears only when the underlying condition
// is resolved through the correct authoritative workflow.
//
// Classifications (see backend classify_funding_evidence):
//   • funding_evidence_missing   — canonical claim of funded, but no
//                                  authoritative funded dollars on
//                                  record.
//   • funding_state_conflict     — funding-status fields disagree
//                                  (e.g. deal_stage still pre-funding
//                                  while funding_status says Funded).
//   • terminal_state_conflict    — Deal claims Paid In Full + All
//                                  Parties Settled but no
//                                  authoritative funded dollars.
//
// deal_amount, approved_amount, and funded_at alone are NEVER treated
// as funded-dollar evidence.  Settlement (paid_in_full /
// all_parties_settled) is intentionally NOT used to clear a row.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, RefreshCw, AlertTriangle, ExternalLink, ShieldCheck, Info } from 'lucide-react';
import { toast } from 'sonner';
import { formatET } from '../utils/timeUtils';

// v3.21-1686 · Classification presentation metadata.  Ordered so the
// most-severe conflict appears first when the operator sorts by class.
const CLASSIFICATIONS = {
  terminal_state_conflict: {
    label:  'Terminal State Conflict',
    tone:   'border-red-300 bg-red-50 text-red-800',
    order:  0,
  },
  funding_state_conflict: {
    label:  'Funding State Conflict',
    tone:   'border-amber-300 bg-amber-50 text-amber-800',
    order:  1,
  },
  funding_evidence_missing: {
    label:  'Funding Evidence Missing',
    tone:   'border-slate-300 bg-slate-50 text-slate-800',
    order:  2,
  },
};

const money = (n) => {
  const v = Number(n || 0);
  return v ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '$0';
};

export default function FundedMismatchesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/funded-mismatches');
      // v3.21-1686 · Order by severity (terminal → conflict → missing),
      // then by last_seen_at descending.
      const sorted = (res.data?.items || []).slice().sort((a, b) => {
        const ao = CLASSIFICATIONS[a.classification]?.order ?? 9;
        const bo = CLASSIFICATIONS[b.classification]?.order ?? 9;
        if (ao !== bo) return ao - bo;
        return String(b.last_seen_at || '').localeCompare(String(a.last_seen_at || ''));
      });
      setItems(sorted);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const onScan = async () => {
    setScanning(true);
    try {
      const res = await api.post('/admin/funded-mismatches/scan');
      const total = res.data?.found ?? 0;
      const cleared = res.data?.cleared_stale ?? 0;
      toast.success(
        `Scan complete — ${total} exception${total === 1 ? '' : 's'} detected` +
        (cleared ? ` · ${cleared} stale row${cleared === 1 ? '' : 's'} cleared` : '')
      );
      await fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="space-y-4 p-6" data-testid="funded-mismatches-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-red-500" />
            Funding Evidence &amp; SSOT Diagnostics
            <Badge variant="outline" className="ml-2 text-[10px] font-medium border-emerald-300 text-emerald-700 bg-emerald-50">
              <ShieldCheck className="h-3 w-3 mr-1 inline" /> Diagnostic-only
            </Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="funding-evidence-summary">
            <strong>Funding Evidence Missing</strong> identifies Deals whose
            authoritative funding state indicates funding occurred but no
            actual funded dollars are recorded in the authoritative funding
            fields.{' '}
            <strong>Funding State Conflict</strong> identifies Deals whose
            funding-related status fields disagree and require review.{' '}
            <strong>Terminal State Conflict</strong> flags Deals claiming Paid
            In Full + All Parties Settled without authoritative funded dollars.
          </p>
        </div>
        <Button
          onClick={onScan}
          disabled={scanning}
          variant="outline"
          data-testid="rescan-mismatches-btn"
        >
          {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {scanning ? 'Scanning…' : 'Re-scan'}
        </Button>
      </div>

      <Card className="border-blue-200 bg-blue-50/40" data-testid="reconcile-diagnostic-banner">
        <CardContent className="p-3 text-xs text-blue-800 flex items-start gap-2">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong className="font-semibold">v3.21-1686 · Authoritative funding SSOT.</strong>{' '}
            Only <code>funded_amount &gt; 0</code> or a{' '}
            <code>funding_sources[]</code> array with real disbursed dollars
            counts as authoritative funded-dollar evidence.
            {' '}
            <code>deal_amount</code>, <code>approved_amount</code>,{' '}
            <code>requested_amount</code>, and offer/approval amounts are
            proposed values — <em>not</em> proof that money funded.
            {' '}
            <code>funded_at</code> alone is supporting metadata — it does
            <em> not</em> establish funded dollars on its own. Settlement
            (<code>paid_in_full</code> / <code>all_parties_settled</code>)
            remains a separate financial state and never substitutes for
            funding evidence. To clear a row, record the real funding
            evidence via the authoritative funding workflow.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center" data-testid="mismatches-empty">
              <p className="text-sm text-slate-500">No diagnostic exceptions. Every funded Deal carries authoritative funded dollars at SSOT.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70">
                  <tr className="border-b border-slate-200">
                    {['Classification', 'Deal', 'Deal ID', 'Stage', 'Status', 'Funded $', 'Sources $', 'Reason', 'Last Seen', ''].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-[11px] font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const cls = CLASSIFICATIONS[row.classification] || {
                      label: row.classification || '—',
                      tone: 'border-slate-300 bg-slate-50 text-slate-700',
                    };
                    const d = row.diagnostic_detail || {};
                    return (
                      <tr
                        key={row.deal_id}
                        className="border-b border-slate-100 hover:bg-slate-50/60"
                        data-testid={`mismatch-row-${row.deal_id}`}
                        data-classification={row.classification || 'unclassified'}
                      >
                        <td className="py-2 px-3">
                          <Badge
                            variant="outline"
                            className={`text-[11px] font-medium ${cls.tone}`}
                            data-testid={`mismatch-classification-${row.deal_id}`}
                          >
                            {cls.label}
                          </Badge>
                        </td>
                        <td className="py-2 px-3 font-medium text-slate-800">{row.deal_name || '—'}</td>
                        <td className="py-2 px-3 font-mono text-[11px] text-slate-500">{row.deal_id}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="border-slate-300 text-slate-700 bg-white">
                            {row.stage || d.deal_stage || '—'}
                          </Badge>
                        </td>
                        <td className="py-2 px-3">
                          <Badge variant="outline">{row.status || d.funding_status || '—'}</Badge>
                        </td>
                        <td className="py-2 px-3 text-xs font-mono text-slate-700" data-testid={`mismatch-funded-${row.deal_id}`}>
                          {money(d.funded_amount)}
                        </td>
                        <td className="py-2 px-3 text-xs font-mono text-slate-700" data-testid={`mismatch-sources-${row.deal_id}`}>
                          {money(d.funding_sources_dollars)}
                        </td>
                        <td className="py-2 px-3 text-xs text-slate-600 max-w-md">{row.classification_reason || row.reason || '—'}</td>
                        <td className="py-2 px-3 text-xs text-slate-500">{row.last_seen_at ? formatET(row.last_seen_at, 'MMM d, h:mm a') : '—'}</td>
                        <td className="py-2 px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Link
                              to={`/clients/${row.deal_id}`}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs"
                              data-testid={`open-deal-${row.deal_id}`}
                            >
                              Open Deal <ExternalLink className="h-3 w-3" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
